import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api';

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Document API
export const documentApi = {
  list: (params?: any) => api.get('/documents', { params }),
  get: (id: string) => api.get(`/documents/${id}`),
  download: (id: string) => api.get(`/documents/${id}/download`, { responseType: 'blob' }),
  update: (id: string, data: any) => api.patch(`/documents/${id}`, data),
  delete: (id: string) => api.delete(`/documents/${id}`),
  getOcr: (id: string) => api.get(`/documents/${id}/ocr`),
};

// Upload API
export const uploadApi = {
  upload: (files: File[]) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadUrl: (url: string, filename?: string) =>
    api.post('/upload/url', { url, filename }),
  getStatus: (documentId: string) =>
    api.get(`/upload/status/${documentId}`),
};

// Search API
export const searchApi = {
  search: (params: any) => api.get('/search', { params }),
  advanced: (criteria: any) => api.post('/search/advanced', criteria),
  suggestions: (query: string) => api.get('/search/suggestions', { params: { q: query } }),
};

// Receipt API
export const receiptApi = {
  get: (documentId: string) => api.get(`/receipts/${documentId}`),
  list: (params?: any) => api.get('/receipts', { params }),
  update: (documentId: string, data: any) =>
    api.patch(`/receipts/${documentId}`, data),
  getByCheck: (checkNumber: string) =>
    api.get(`/receipts/check/${checkNumber}`),
  getStats: (params?: any) => api.get('/receipts/stats', { params }),
};

// Reconciliation API
export const reconciliationApi = {
  uploadStatement: (file: File, accountType: string, accountLastFour?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('accountType', accountType);
    if (accountLastFour) formData.append('accountLastFour', accountLastFour);
    return api.post('/reconciliation/statement', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  autoMatch: (dateFrom: string, dateTo: string, threshold?: number) =>
    api.post('/reconciliation/auto-match', { dateFrom, dateTo, threshold }),
  manualMatch: (transactionId: number, receiptId: string, notes?: string) =>
    api.post('/reconciliation/match', { transactionId, receiptId, notes }),
  unmatch: (matchId: number) =>
    api.delete(`/reconciliation/match/${matchId}`),
  getMissing: (params?: any) =>
    api.get('/reconciliation/missing', { params }),
  getSummary: (params?: any) =>
    api.get('/reconciliation/summary', { params }),
};

// Category API
export const categoryApi = {
  list: () => api.get('/categories'),
  getTree: () => api.get('/categories/tree'),
  create: (data: any) => api.post('/categories', data),
  update: (id: number, data: any) => api.patch(`/categories/${id}`, data),
  delete: (id: number) => api.delete(`/categories/${id}`),
  getDocuments: (id: number, params?: any) =>
    api.get(`/categories/${id}/documents`, { params }),
};

// Archive API
export const archiveApi = {
  archive: (documentId: string) => api.post(`/archive/${documentId}`),
  restore: (documentId: string) => api.post(`/archive/${documentId}/restore`),
  autoArchive: () => api.post('/archive/auto'),
  getStats: () => api.get('/archive/stats'),
};

// Analytics API
export const analyticsApi = {
  getDashboard: (timeframe?: number) =>
    api.get('/analytics/dashboard', { params: { timeframe } }),
  getSpending: (params: any) => api.get('/analytics/spending', { params }),
  getTaxReport: (year: string) => api.get('/analytics/tax', { params: { year } }),
  export: (type: string, from: string, to: string) =>
    api.get('/analytics/export', {
      params: { type, from, to },
      responseType: 'blob',
    }),
};

// Migration API
export const migrationApi = {
  importEvernote: (exportPath: string, authToken?: string) =>
    api.post('/migration/evernote', { exportPath, authToken }),
  bulkImport: (files: File[]) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    return api.post('/migration/bulk', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getStatus: () => api.get('/migration/status'),
  getEvernoteInstructions: () => api.get('/migration/evernote/instructions'),
};

